import React, { memo, useEffect } from 'react';
import { Grid, Typography, TextField, MenuItem, Divider } from '@material-ui/core';
import { useFormikContext, Field } from 'formik';
import { useCampfireQuery } from '../../../../../../../../../global/network/useCampfireQuery';
import { useCampfireLazyQuery } from '../../../../../../../../../global/network/useCampfireLazyQuery';
import {
  ADMIN_CONSOLE_GET_ALL_TASKS_LIGHTWEIGHT,
  ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK,
} from '../../../../admin-console-volunteer-profile.gql';
import { FieldValues } from '../FieldTaskItemDialog';

export const CrossTaskDynamicAdditionalFields = memo(() => {
  const { values, setFieldValue } = useFormikContext<FieldValues>();

  // Get all tasks for the dropdown
  const { data, loading } = useCampfireQuery(ADMIN_CONSOLE_GET_ALL_TASKS_LIGHTWEIGHT);

  // Lazy query for getting detailed task info when a task is selected
  const [getTaskDetails, { data: taskDetailsData, loading: taskDetailsLoading }] = useCampfireLazyQuery(
    ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK
  );

  if (values.field.__typename !== 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
    return null;
  }

  // Create task options for dropdown
  const taskOptions =
    data?.vm?.cakes?.flatMap(
      (cake) =>
        cake.allTasks
          ?.filter((task) => !task.isHidden)
          ?.map((task) => ({
            value: task.taskId,
            label: `${cake.title} - ${task.title}`,
            cakeTitle: cake.title,
            taskTitle: task.title,
          })) || []
    ) || [];

  // Get the currently selected task ID
  const selectedTaskId = values.field.sourceTaskId;

  // Function to handle task selection and fetch its details
  const handleTaskSelect = (taskId: string) => {
    if (taskId && taskId !== selectedTaskId) {
      getTaskDetails({
        variables: { taskId },
      });
    }
  };

  // Load existing task details when editing (when sourceTaskId exists but task details haven't been loaded)
  useEffect(() => {
    if (selectedTaskId && !taskDetailsData?.vm?.task) {
      getTaskDetails({
        variables: { taskId: selectedTaskId },
      });
    }
  }, [selectedTaskId, taskDetailsData?.vm?.task, getTaskDetails]);

  // Extract field options from the selected task (only fields with options)
  const getFieldOptionsFromTask = () => {
    const task = taskDetailsData?.vm?.task;
    if (!task) return [];

    const taskItems = task.taskItems || [];

    return taskItems
      .filter((taskItem) => taskItem.__typename === 'VOLUNTEER_TaskItemFieldType' && taskItem.field)
      .map((taskItem) => {
        const { field } = taskItem;
        let hasOptions = false;
        let fieldType = '';

        switch (field.__typename) {
          case 'VOLUNTEER_DropdownTaskFieldType':
            hasOptions = field.dropdownOptions?.length > 0;
            fieldType = 'Dropdown';
            break;
          case 'VOLUNTEER_MultipleChoiceTaskFieldType':
            hasOptions = field.multipleChoiceOptions?.length > 0;
            fieldType = 'Multiple Choice';
            break;
          case 'VOLUNTEER_CheckboxTaskFieldType':
            hasOptions = field.checkboxOptions?.length > 0;
            fieldType = 'Checkbox';
            break;
          case 'VOLUNTEER_QuizTaskFieldType':
            hasOptions = field.quizTaskFieldOptions?.length > 0;
            fieldType = 'Quiz';
            break;
          default:
            hasOptions = false;
            fieldType = 'Unknown';
            break;
        }

        return hasOptions
          ? {
              value: field.taskFieldId,
              label: `${field.name} (${fieldType})`,
              fieldName: field.name,
              fieldType: field.__typename,
            }
          : null;
      })
      .filter((item) => item !== null);
  };

  const fieldOptions = getFieldOptionsFromTask();

  // Get options from the selected reference field
  const getReferenceFieldOptions = () => {
    const task = taskDetailsData?.vm?.task;
    const selectedFieldId = values.field.sourceFieldId;

    if (!task || !selectedFieldId) return [];

    const taskItems = task.taskItems || [];

    // Find the selected field
    const selectedField = taskItems
      .filter((taskItem) => taskItem.__typename === 'VOLUNTEER_TaskItemFieldType' && taskItem.field)
      .map((taskItem) => taskItem.field)
      .find((field) => field.taskFieldId === selectedFieldId);

    if (!selectedField) return [];

    // Extract options based on field type
    switch (selectedField.__typename) {
      case 'VOLUNTEER_DropdownTaskFieldType':
        return (
          selectedField.dropdownOptions?.map((option) => ({
            value: option.dropdownFieldOptionId,
            label: option.name,
            order: option.order,
          })) || []
        );
      case 'VOLUNTEER_MultipleChoiceTaskFieldType':
        return (
          selectedField.multipleChoiceOptions?.map((option) => ({
            value: option.multipleChoiceFieldOptionId,
            label: option.name,
            order: option.order,
          })) || []
        );
      case 'VOLUNTEER_CheckboxTaskFieldType':
        return (
          selectedField.checkboxOptions?.map((option) => ({
            value: option.checkboxFieldOptionId,
            label: option.name,
            order: option.order,
          })) || []
        );
      case 'VOLUNTEER_QuizTaskFieldType':
        return (
          selectedField.quizTaskFieldOptions?.map((option) => ({
            value: option.quizTaskFieldOptionId,
            label: option.name,
            order: option.order,
          })) || []
        );
      default:
        return [];
    }
  };

  const referenceFieldOptions = getReferenceFieldOptions();

  // Automatically populate crossTaskDynamicOptions when reference field options become available
  useEffect(() => {
    if (referenceFieldOptions.length > 0 && values.field.sourceFieldId) {
      const currentOptions = values.field.crossTaskDynamicOptions || [];

      // Check if we need to update the options (when options change or are empty)
      const optionValues = referenceFieldOptions.map((opt) => opt.value).sort();
      const currentOptionValues = currentOptions.map((opt: any) => opt.sourceOptionValue).sort();

      const needsUpdate =
        optionValues.length !== currentOptionValues.length ||
        !optionValues.every((val, idx) => val === currentOptionValues[idx]);

      if (needsUpdate) {
        const newOptions = referenceFieldOptions.map((option) => ({
          sourceOptionValue: option.value,
          fieldConfiguration: [{}], // Provide minimal object to satisfy validation
        }));

        setFieldValue('field.crossTaskDynamicOptions', newOptions);
      }
    }
  }, [referenceFieldOptions, values.field.sourceFieldId, values.field.crossTaskDynamicOptions, setFieldValue]);

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <Typography variant='subtitle2' gutterBottom>
          Cross-Task Dynamic Field Configuration
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          Select a task to reference for dynamic field behavior
        </Typography>
      </Grid>

      <Grid item>
        <Field name='field.sourceTaskId'>
          {({ field, meta, form }) => (
            <TextField
              {...field}
              select
              fullWidth
              label='Reference Task'
              helperText="Choose which task's field will control this field's visibility"
              disabled={loading}
              error={meta.touched && !!meta.error}
              onChange={(e) => {
                field.onChange(e);
                handleTaskSelect(e.target.value);
                // Clear the field selection when task changes
                form.setFieldValue('field.sourceFieldId', '');
              }}
            >
              {taskOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Field>
      </Grid>

      {selectedTaskId && (
        <Grid item>
          <Field name='field.sourceFieldId'>
            {({ field, meta }) => (
              <TextField
                {...field}
                select
                fullWidth
                label='Reference Field'
                helperText='Choose which field from the selected task will control visibility'
                disabled={taskDetailsLoading}
                error={meta.touched && !!meta.error}
              >
                {fieldOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Field>
        </Grid>
      )}

      {values.field.sourceFieldId && referenceFieldOptions.length > 0 && (
        <>
          <Grid item>
            <Divider />
          </Grid>

          <Grid item>
            <Typography variant='subtitle2' gutterBottom>
              Options
            </Typography>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              The following options will be available as triggers after saving this field:
            </Typography>

            <Grid container direction='column' spacing={1}>
              {referenceFieldOptions.map((option) => (
                <Grid item key={option.value}>
                  <Typography variant='body2'>• {option.label}</Typography>
                </Grid>
              ))}
            </Grid>

            <Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
              After adding this field, you can configure what content appears for each option using the trigger system.
            </Typography>
            <Typography variant='body2' color='primary' style={{ marginTop: 4, fontWeight: 'bold' }}>
              ✓ {referenceFieldOptions.length} trigger options will be created
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
});
